import { SheetSyncEvents, SHEET_OPACITY, FADE_DURATION } from './SheetSyncConstants.js';
import { warnOnce } from '../../../src/application/LoggingUtils.js';

const INFINITE_BOX = new THREE.Box3().setFromPoints([new THREE.Vector3(-1e20, -1e20, -1e20), new THREE.Vector3(1e20, 1e20, 1e20)]);

/**
// SheetVisibilityManager - Manage the visibility of the sheet - opacity and crop.
*/
export default class SheetVisibilityManager {
    constructor(viewer, options) {
        this.viewer = viewer;
        this.options = options;
        const disableAnimations = options.disableAnimations ?? false;
        this.defaultDuration = disableAnimations ? 0 : FADE_DURATION;
    }

    // Update sheet's opacity and crop bounds.
    updateSheetAppearance(sheetMap, visible) {
        const model = this.viewer.model;

        if (!model) {
            return;
        }

        const targetOpacity = visible ? SHEET_OPACITY : 0.0;
        const viewportBounds = sheetMap && sheetMap.viewportBounds;

        if (visible) {
            this.updateCrop(visible, viewportBounds);
        }

        this.animateOpacity(this.viewer.model, targetOpacity, () => {
            this.updateCrop(visible, viewportBounds);
        });
    }

    // Set sheet visibility immediately.
    setVisibilityImmediate(visible) {
        const models = this.viewer.getAllModels();

        models.forEach(model => {
            if (model.fadeAnim) {
                model.fadeAnim.stop();
                model.fadeAnim = null;
            }

            this.setModelOpacity(model, visible ? 1.0 : 0.0);
        });
    }

    // Animate sheet's opacity.
    animateOpacity(model, targetValue, onFadeDone, duration = this.defaultDuration) {
        if (!model) {
            return;
        }

        if (model.fadeAnim) {
            model.fadeAnim.stop();
            model.fadeAnim = null;
        }

        const startVal = model.opacity || 0.0;

        const onTimer = (t) => {
            t = Autodesk.Viewing.Private.smootherStep(t);
            const opacity = Autodesk.Viewing.Private.lerp(startVal, targetValue, t);
            this.setModelOpacity(model, opacity);
        };

        const onFadeEnd = () => {
            model.fadeAnim = null;
            onFadeDone && onFadeDone();
        };

        model.fadeAnim = Autodesk.Viewing.Private.fadeValue(0, 1, duration, onTimer, onFadeEnd);
        model.fadeAnim.targetValue = targetValue;
    }

    // Change all sheet's materials to be in with a given opacity.
    setModelOpacity(model, value) {
        if (!this.options.hideSheetWhenNotFocused) {
            return;
        }

        if (model) {
            const materialsManager = this.viewer.impl.matman();
            const materials = materialsManager.getModelMaterials(model).mats;

            let changed = false;

            Object.keys(materials).forEach((key) => {
                const material = materials[key];
                if (!material.uniforms) {
                    warnOnce(
                        'Encountered a material without uniforms when setting model opacity. ' +
                        'Only THREE.ShaderMaterials have uniforms that can be updated.');
                    return;
                }
                if (material.opacity !== value) {
                    material.opacity = value;
                    material.uniforms.opacity.value = value;
                    material.uniformsNeedUpdate = true;
                    changed = true;
                }
            });

            model.opacity = value;

            if (changed) {
                this.viewer.dispatchEvent({
                    type: SheetSyncEvents.SHEET_VISIBILITY_CHANGED_EVENT,
                    model,
                    value,
                });

                this.viewer.impl.invalidate(true);
            }
        }
    }

    expandCropBounds(sheetMap, duration = this.defaultDuration) {
        const startBounds = sheetMap.viewportBounds;
        const modelBounds = this.viewer.model.getBoundingBox();

        const box = new THREE.Box2();

        const onTimer = (t) => {
            t = Autodesk.Viewing.Private.smootherStep(t);
            box.min.lerpVectors(startBounds.min, modelBounds.min, t);
            box.max.lerpVectors(startBounds.max, modelBounds.max, t);
            this.updateCrop(true, box);
        };

        Autodesk.Viewing.Private.fadeValue(0, 1, duration, onTimer);
    }

    // Update the crop according to given viewport bounds.
    updateCrop(visible, bounds = INFINITE_BOX) {
        if (this.options.cropToViewport) {
            const model = this.viewer.model;

            if (!model) {
                return;
            }

            if (!model.latestCropBounds) {
                model.latestCropBounds = new THREE.Box3();
            }

            let cropBounds;
            if (visible) {
                cropBounds = bounds;
            } else {
                // put the cut planes in infinity.
                // This is more efficient then removing the cutplanes from all the materials.
                cropBounds = INFINITE_BOX;
            }

            if (!model.latestCropBounds.equals(cropBounds)) {
                model.latestCropBounds = cropBounds.clone();
                this.viewer.impl.setViewportBounds(model, cropBounds.isEmpty() ? null : cropBounds);
            }
        }
    }

    isSheetVisible() {
        return this.viewer?.model?.opacity !== 0;
    }

    setSheetOpaque(duration) {
        this.animateOpacity(this.viewer.model, 1.0, undefined, duration);

        const onTimer = (t) => {
            t = Autodesk.Viewing.Private.smootherStep(t);
            const opacity = Autodesk.Viewing.Private.lerp(0, 1, t);
            this.viewer.impl && this.viewer.setBackgroundOpacity(opacity);
        };

        Autodesk.Viewing.Private.fadeValue(0, 1, duration, onTimer);
    }
}
